// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useMemo } from 'react';
import { InlineLoading } from '@carbon/react';
import { useSiteApprovals } from '../../hooks/useSiteApprovals';
import { useActiveSite } from '../../hooks/useActiveSite';
import { usePersona } from '../../hooks/usePersona';
import { useHomeTabCounts } from '../../hooks/useHomeTabCounts';
import { PageHead, EmptyState } from '../../components/shell/ScreenHelpers';
import { AlarmTabs } from '../../components/shell/alarms/AlarmTabs';
import PlanCard from '../../components/shell/PlanCard';
import { imminentPlans, pendingReviewPlans } from '../../services/planSelectors';
import { useSiteTime } from '../../hooks/useSiteTime';
import type { HitlApproval } from '../../types';
import NoAccess from '../../components/common/NoAccess';
import { isNoAccessMessage } from '../../services/api';

/**
 * Home › Recommendations. Role-aware over the shared plan-approval list:
 *
 * - **Ops Manager** sees plans awaiting their decision (`PENDING`) that execute
 *   today or later — a plan whose execution day has passed can no longer be
 *   approved into effect. The outstanding count surfaces as the purple badge on
 *   the Recommendations tab.
 * - **Operator** sees what's about to run: plans whose `executionStart` falls on
 *   today or tomorrow, drawn from the last 7 days of raised plans. `REJECTED`
 *   ones are included for visibility into the decision, but only while their
 *   execution day is still ahead. The approved archive lives on Home › Closed.
 *
 * Uses the same `AlarmTabs` chrome as the rest of Home. Loading is non-blocking:
 * the tab bar and header paint immediately and an inline indicator shows only
 * when there's nothing to display, so navigating here never blanks the shell
 * and a background poll never flickers a spinner over the cards.
 */
export default function Recommendations() {
  const { siteLabel } = useActiveSite();
  const { persona } = usePersona();
  const counts = useHomeTabCounts();
  const { approvals, isFetching, error } = useSiteApprovals();

  const isManager = persona === 'manager';
  const { siteZone } = useSiteTime();

  const items = useMemo<HitlApproval[]>(() => {
    if (isManager) {
      // The manager's review queue: what still needs a decision and can still
      // be acted on. Defined in planSelectors so the tab badge counts exactly
      // what this list renders.
      return pendingReviewPlans(approvals, undefined, siteZone);
    }
    // Operator: the imminent queue, defined once in planSelectors so the tab
    // badge counts exactly what this list renders.
    return imminentPlans(approvals, undefined, siteZone);
  }, [approvals, isManager, siteZone]);

  const dateline = isManager
    ? `${siteLabel} · plans awaiting your approval, executing today & later`
    : `${siteLabel} · approved plans executing today & tomorrow`;

  const emptyState = isManager
    ? {
        big: 'No plans awaiting approval.',
        sub: 'Plans executing today or later appear here when Helios raises them for review.',
      }
    : {
        big: 'Nothing executing today or tomorrow.',
        sub: 'Approved plans scheduled to execute today or tomorrow appear here. Every approved plan for this site is listed under Closed.',
      };

  return (
    <div className="hs-screen">
      <AlarmTabs active="recommendations" counts={counts} />

      <div className="hs-screen-body">
        <PageHead title="Recommendations" dateline={dateline} />

        {/* A dial shows only when there's nothing to display; once cards are on
            screen a background poll never flickers a spinner over them. */}
        {items.length > 0 ? (
          <div className="hs-tiles-plan">
            {items.map(a => (
              <PlanCard key={a.approvalId} approval={a} showApprovedOn={a.state === 'APPROVED'} />
            ))}
          </div>
        ) : isFetching ? (
          <InlineLoading
            className="hs-bg-loading"
            description="Loading recommendations…"
            status="active"
          />
        ) : error && isNoAccessMessage(error) ? (
          <NoAccess big="You don't have access to recommendations" />
        ) : error ? (
          <EmptyState big="Couldn't load recommendations." sub={error} />
        ) : (
          <EmptyState big={emptyState.big} sub={emptyState.sub} />
        )}
      </div>
    </div>
  );
}
